/**
 * NDT field tickets on the crew's phone (batch 4, fieldiq-api#1157).
 *
 *  - `NdtFieldJobTickets` — "Today's ticket" on /field/ndt/jobs/:id: the job's
 *    tickets (GET /field/jobs/:id/tickets) and, on an active per-day job,
 *    "Close out today" (the tenant's today from meta, never the phone's).
 *  - `NdtFieldTicketScreen` — /field/ndt/tickets/:id: the lines, "Add from rate
 *    card", change a quantity, remove a line, and "Get company man signature" —
 *    a full-screen sign page the company man reads and signs on this phone.
 *
 * QUANTITIES ARE THE CREW'S, PRICES ARE THE OFFICE'S. Nothing here sends a
 * rate or an amount: each line's money column is the API's verdict — an
 * amount, "No charge" or "No rate" — never quantity × rate worked out here.
 *
 * SIGNING IS THE API'S CALL. Offered on a draft; a ticket with no lines or an
 * unpriced line is refused (`no_lines`, `unrated_lines`) and the sentence is
 * shown on the sign page. The signature goes as a PNG data URL
 * (`signature_data`), the path PT's field ticket uses. A signed ticket is
 * read-only — the status the API sent, not a lock decided here.
 *
 * ONLINE ONLY. Nothing here is queued for later; in a dead zone each write says
 * it needs signal instead of pretending to save.
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CalendarCheck, CheckCircle2, ChevronRight, Loader2, PenLine, Plus, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import SignaturePad from '../../../components/SignaturePad';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useAddNdtFieldTicketLine, useCloseNdtFieldDay, useNdtFieldJobTickets, useNdtFieldTicket, useSignNdtFieldTicket } from '../../../hooks/useNdtTickets';
import { formatCalendarDate, formatInstant } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { useSyncStore } from '../../../lib/offline/sync-store';
import {
  canPriceNdtTickets,
  canWorkNdtTickets,
  ndtLineAmountText,
  ndtLineNeedsPrice,
  ndtQtyText,
  ndtSubtotalText,
  ndtTicketRefusal,
  ndtTicketScopeText,
  ndtTicketStatusSkin,
} from '../../../lib/ndt-tickets';
import type { NdtFieldJob } from '../../../types/ndt-inspection';
import type { NdtFieldTicketDetail, NdtFieldTicketLine, NdtTicketSign } from '../../../types/ndt-tickets';
import { NDT_INPUT } from '../NdtBits';
import { NdtAddLineForm, NdtEditLineForm, NdtRemoveLineForm } from '../NdtTicketBits';
import {
  PT_FIELD_PRIMARY,
  PT_FIELD_SECONDARY,
  PtFieldBadge,
  PtFieldCard,
  PtFieldEmpty,
  PtFieldPage,
  PtFieldQueryState,
  PtFieldSectionTitle,
  PtRefusal,
} from '../../pressure-testing/field/PtFieldBits';

export const ndtFieldTicketPath = (ticketId: string) => `/field/ndt/tickets/${ticketId}`;

function OfflineNote({ what }: { what: string }) {
  return (
    <p role="status" data-testid="ndt-field-ticket-offline" className="rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
      Needs signal to {what}. It is not saved for later.
    </p>
  );
}

// ─── On the job screen ───────────────────────────────────────────────────────

export function NdtFieldJobTickets({ job }: { job: Pick<NdtFieldJob, 'id' | 'status' | 'ticket_mode'> }) {
  const { user } = useAuth();
  const isOnline = useSyncStore((s) => s.isOnline);
  const tickets = useNdtFieldJobTickets(job.id);
  const closeDay = useCloseNdtFieldDay();
  const [refusal, setRefusal] = useState<string | null>(null);
  const rows = tickets.data?.data ?? [];
  const today = tickets.data?.meta.today ?? null;
  const failure = queryLoadFailure(tickets);
  const todays = rows.find((t) => t.scope === 'day' && t.work_date === today && t.status !== 'void') ?? null;
  const perDay = job.ticket_mode === 'per_day';

  if (job.status === 'cancelled') return null;

  const onCloseDay = () => {
    if (!today) return;
    setRefusal(null);
    closeDay.mutate(
      { jobId: job.id, workDate: today },
      {
        onSuccess: (res) => toast.success(res.data?.ticket_number ? `Today is on ${res.data.ticket_number}` : 'Today is closed out'),
        onError: (err) => setRefusal(ndtTicketRefusal(err, 'Today was not closed out. Try again.').message),
      },
    );
  };

  return (
    <section className="mt-6" data-testid="ndt-field-job-tickets">
      <PtFieldSectionTitle>Today's ticket</PtFieldSectionTitle>
      {tickets.isLoading ? (
        <Loader2 className="size-5 animate-spin text-fieldiq-muted" aria-label="Loading this job's tickets" />
      ) : failure ? (
        <LoadFailureNotice error={queryFailureError(tickets)} kind={failure} subject="this job's tickets" />
      ) : (
        <>
          {!todays && (
            <PtFieldEmpty>
              {perDay ? 'Today is not closed out yet. Close it out when the day’s work is done.' : 'The field ticket is made when the job is completed.'}
            </PtFieldEmpty>
          )}
          <div className="space-y-2">
            {rows.map((t) => {
              const skin = ndtTicketStatusSkin(t.status);
              return (
                <Link key={t.id} to={ndtFieldTicketPath(t.id)} className="block" data-testid="ndt-field-job-ticket">
                  <PtFieldCard className={`flex min-h-[72px] items-center gap-3 border-l-4 px-4 py-3 active:bg-fieldiq-dark/50 ${skin.rail}`}>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-base font-bold text-white">{t.ticket_number}</span>
                        <PtFieldBadge skin={skin} />
                        {t.id === todays?.id && <span className="text-xs font-bold uppercase tracking-[0.1em] text-fieldiq-muted">Today</span>}
                      </div>
                      <p className="mt-1 text-sm text-fieldiq-text">
                        {ndtTicketScopeText(t.scope, t.work_date)} · {ndtSubtotalText(t.subtotal, t.line_count, t.currency)}
                      </p>
                    </div>
                    <ChevronRight className="size-6 shrink-0 text-fieldiq-muted" aria-hidden="true" />
                  </PtFieldCard>
                </Link>
              );
            })}
          </div>
        </>
      )}

      {perDay && job.status === 'active' && canWorkNdtTickets(user?.role) && (
        <div className="mt-3 space-y-2">
          <PtRefusal message={refusal} testId="ndt-field-close-day-refusal" />
          {!isOnline ? (
            <OfflineNote what="close out the day" />
          ) : (
            <button type="button" className={PT_FIELD_PRIMARY} onClick={onCloseDay} disabled={!today || closeDay.isPending} data-testid="ndt-field-close-day">
              {closeDay.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <CalendarCheck className="size-5" aria-hidden="true" />}
              {todays ? 'Add today’s new work' : 'Close out today'}
              {today ? ` (${formatCalendarDate(today) ?? today})` : ''}
            </button>
          )}
        </div>
      )}
    </section>
  );
}

// ─── The ticket ──────────────────────────────────────────────────────────────

function LineCard({ line, currency, editable, onEdit, onRemove }: { line: NdtFieldTicketLine; currency: string | null; editable: boolean; onEdit: () => void; onRemove: () => void }) {
  const warn = ndtLineNeedsPrice(line);
  return (
    <PtFieldCard className={`border-l-4 px-4 py-3 ${warn ? 'border-l-fi-amber' : 'border-l-transparent'}`}>
      <div data-testid="ndt-field-ticket-line" data-money={line.money}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-base font-bold text-white">{line.description}</p>
            {line.weld_number && <p className="mt-0.5 text-xs text-fieldiq-muted">Weld {line.weld_number}</p>}
          </div>
          <p className={`shrink-0 text-right text-base tabular-nums ${warn ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`} data-testid="ndt-field-ticket-line-amount">
            {ndtLineAmountText(line, currency)}
          </p>
        </div>
        <div className="mt-2 flex items-center justify-between gap-3">
          {editable ? (
            <button
              type="button"
              onClick={onEdit}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-fi-control border border-fieldiq-border bg-fieldiq-dark/40 px-3 font-mono text-lg font-bold tabular-nums text-white"
              aria-label={`Change quantity, ${line.description}`}
              data-testid="ndt-field-ticket-line-qty"
            >
              {ndtQtyText(line.quantity, line.unit, line.distance_unit)}
            </button>
          ) : (
            <span className="font-mono text-lg font-bold tabular-nums text-white">{ndtQtyText(line.quantity, line.unit, line.distance_unit)}</span>
          )}
          {editable && (
            <button type="button" onClick={onRemove} className="inline-flex size-11 items-center justify-center rounded-fi-control text-fieldiq-muted" aria-label={`Remove ${line.description}`} data-testid="ndt-field-ticket-line-remove">
              <Trash2 className="size-5" aria-hidden="true" />
            </button>
          )}
        </div>
        {warn && line.unpriced_reason && <p className="mt-2 text-sm text-fieldiq-text">{line.unpriced_reason}</p>}
      </div>
    </PtFieldCard>
  );
}

/** The full-screen page the company man reads and signs. */
function CompanyManSign({ ticket, onClose }: { ticket: NdtFieldTicketDetail; onClose: () => void }) {
  const sign = useSignNdtFieldTicket();
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [po, setPo] = useState(ticket.po_number ?? '');
  const [afe, setAfe] = useState(ticket.afe_number ?? '');
  const [costCode, setCostCode] = useState(ticket.cost_code ?? '');
  const [data, setData] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const submit = () => {
    if (!name.trim()) return setError("Enter the company man's name.");
    if (!data) return setError('Draw the signature and tap Confirm Signature.');
    setError(null);
    const body: NdtTicketSign = { signer_name: name.trim(), signer_title: title.trim() || null, signature_data: data };
    // Each reference is sent only when it changed: absent keeps the ticket's own, a cleared one sends null.
    if (po.trim() !== (ticket.po_number ?? '')) body.po_number = po.trim() || null;
    if (afe.trim() !== (ticket.afe_number ?? '')) body.afe_number = afe.trim() || null;
    if (costCode.trim() !== (ticket.cost_code ?? '')) body.cost_code = costCode.trim() || null;
    sign.mutate(
      { ticketId: ticket.id, body },
      {
        onSuccess: () => {
          toast.success(`${ticket.ticket_number} signed`);
          onClose();
        },
        onError: (err) => setError(ndtTicketRefusal(err, 'The ticket was not signed. Try again.').message),
      },
    );
  };

  const input = `${NDT_INPUT} h-12 text-base`;
  const label = 'mb-1 block text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted';
  return (
    <div role="dialog" aria-modal="true" aria-label={`Sign ${ticket.ticket_number}`} className="fixed inset-0 z-[60] overflow-y-auto bg-fieldiq-dark" data-testid="ndt-field-sign-screen">
      <div className="mx-auto max-w-3xl px-4 pb-10 pt-4">
        <div className="flex items-center justify-between gap-3">
          <p className="text-[0.7rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Field ticket for signature</p>
          <button type="button" onClick={onClose} aria-label="Close" className="inline-flex size-11 items-center justify-center rounded-fi-control text-fieldiq-text">
            <X className="size-6" aria-hidden="true" />
          </button>
        </div>
        <h2 className="font-mono text-3xl font-extrabold text-white">{ticket.ticket_number}</h2>
        <p className="mt-1 text-lg text-fieldiq-text">
          {ticket.customer_name || '—'} · {ticket.job_number || '—'}
        </p>
        <p className="text-base text-fieldiq-text">
          {[ticket.location_name, ticket.lsd].filter(Boolean).join(' · ') || '—'} · {ndtTicketScopeText(ticket.scope, ticket.work_date)}
        </p>

        <ul className="mt-5 divide-y divide-fieldiq-border rounded-fi-panel border border-fieldiq-border bg-fieldiq-card" data-testid="ndt-field-sign-lines">
          {ticket.lines.map((l) => (
            <li key={l.id} className="flex items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0">
                <p className="text-lg font-bold text-white">{l.description}</p>
                <p className="font-mono text-base text-fieldiq-text">{ndtQtyText(l.quantity, l.unit, l.distance_unit)}</p>
              </div>
              <p className={`shrink-0 text-lg tabular-nums ${ndtLineNeedsPrice(l) ? 'font-bold text-fieldiq-text' : 'font-mono text-white'}`}>{ndtLineAmountText(l, ticket.currency)}</p>
            </li>
          ))}
          {ticket.lines.length === 0 && <li className="px-4 py-3 text-base text-fieldiq-text">No charges.</li>}
          <li className="flex items-center justify-between gap-3 px-4 py-4">
            <p className="text-sm font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal (before tax)</p>
            <p className="font-mono text-2xl font-extrabold tabular-nums text-white" data-testid="ndt-field-sign-subtotal">
              {ndtSubtotalText(ticket.subtotal, ticket.line_count, ticket.currency)}
            </p>
          </li>
        </ul>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className={label}>Company man's name</span>
            <input className={input} value={name} onChange={(e) => setName(e.target.value)} maxLength={200} autoComplete="off" data-testid="ndt-field-sign-name" />
          </label>
          <label className="block">
            <span className={label}>Title</span>
            <input className={input} value={title} onChange={(e) => setTitle(e.target.value)} maxLength={100} data-testid="ndt-field-sign-title" />
          </label>
          <label className="block">
            <span className={label}>PO</span>
            <input className={`${input} font-mono`} value={po} onChange={(e) => setPo(e.target.value)} maxLength={100} data-testid="ndt-field-sign-po" />
          </label>
          <label className="block">
            <span className={label}>AFE</span>
            <input className={`${input} font-mono`} value={afe} onChange={(e) => setAfe(e.target.value)} maxLength={100} data-testid="ndt-field-sign-afe" />
          </label>
          <label className="block sm:col-span-2">
            <span className={label}>Cost code</span>
            <input className={`${input} font-mono`} value={costCode} onChange={(e) => setCostCode(e.target.value)} maxLength={100} data-testid="ndt-field-sign-cost-code" />
          </label>
        </div>

        <div className="mt-5">
          <SignaturePad label="Company man's signature" onSave={setData} onClear={() => setData(null)} />
        </div>
        <div className="mt-4 space-y-3">
          <PtRefusal message={error} testId="ndt-field-sign-error" />
          <button type="button" className={PT_FIELD_PRIMARY} onClick={submit} disabled={sign.isPending} data-testid="ndt-field-sign-submit">
            {sign.isPending ? <Loader2 className="size-5 animate-spin" aria-hidden="true" /> : <PenLine className="size-5" aria-hidden="true" />}
            Sign
          </button>
          <button type="button" className={PT_FIELD_SECONDARY} onClick={onClose} disabled={sign.isPending}>
            Back to the ticket
          </button>
        </div>
      </div>
    </div>
  );
}

type Panel = { kind: 'add' } | { kind: 'edit'; line: NdtFieldTicketLine } | { kind: 'remove'; line: NdtFieldTicketLine } | { kind: 'sign' };

export default function NdtFieldTicketScreen() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isOnline = useSyncStore((s) => s.isOnline);
  const query = useNdtFieldTicket(id);
  const addLine = useAddNdtFieldTicketLine();
  const ticket = query.data?.data ?? null;
  const tz = query.data?.meta.time_zone ?? null;
  const today = query.data?.meta.today ?? null;
  const [panel, setPanel] = useState<Panel | null>(null);
  const notFound = (queryFailureError(query) as { response?: { status?: number } } | null)?.response?.status === 404;
  const isDraft = ticket?.status === 'draft';
  const canWork = canWorkNdtTickets(user?.role);
  const editable = !!ticket && isDraft && isOnline && canWork;

  return (
    <PtFieldPage title={ticket?.ticket_number || 'Field ticket'} backTo={ticket ? `/field/ndt/jobs/${ticket.job_id}` : '/field/ndt'} testId="ndt-field-ticket">
      {notFound ? (
        <PtFieldEmpty>This field ticket was not found.</PtFieldEmpty>
      ) : (
        <PtFieldQueryState query={query} subject="this field ticket">
          {ticket && (
            <>
              <PtFieldCard className="px-4 py-4">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-lg font-extrabold text-fieldiq-text">{ticket.ticket_number}</span>
                  <PtFieldBadge skin={ndtTicketStatusSkin(ticket.status)} testId="ndt-field-ticket-status" />
                </div>
                <p className="mt-1 text-lg font-bold text-white">{ticket.customer_name || '—'}</p>
                <p className="mt-0.5 text-sm text-fieldiq-text">
                  {ticket.job_number || '—'} · {ndtTicketScopeText(ticket.scope, ticket.work_date)}
                </p>
              </PtFieldCard>

              {ticket.status === 'void' && (
                <p className="mt-3 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-steel bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  This ticket is void. {ticket.void_reason || ''}
                </p>
              )}
              {ticket.signature && (
                <p data-testid="ndt-field-ticket-signed" className="mt-3 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-blue bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  Signed by {ticket.signature.signer_name || '—'}
                  {ticket.signature.signer_title ? `, ${ticket.signature.signer_title}` : ''} — {formatInstant(ticket.signature.signed_at, tz) ?? '—'}. A signed ticket cannot change; the office voids it and makes a new one to correct it.
                </p>
              )}

              <PtFieldSectionTitle>Charges</PtFieldSectionTitle>
              {ticket.lines.length === 0 ? (
                <PtFieldEmpty>No charges on this ticket yet.</PtFieldEmpty>
              ) : (
                <div className="space-y-2" data-testid="ndt-field-ticket-lines">
                  {ticket.lines.map((l) =>
                    panel && (panel.kind === 'edit' || panel.kind === 'remove') && panel.line.id === l.id ? (
                      <PtFieldCard key={l.id} className="px-4 py-4">
                        {panel.kind === 'edit' ? (
                          <NdtEditLineForm
                            line={{ id: l.id, quantity: l.quantity, description: l.description, source: l.source, rate: l.rate, rateSource: l.rate_source, unit: l.unit, distanceUnit: l.distance_unit }}
                            currency={ticket.currency}
                            canPrice={false}
                            bigControls
                            onDone={() => setPanel(null)}
                            onCancel={() => setPanel(null)}
                          />
                        ) : (
                          <NdtRemoveLineForm line={{ id: l.id, description: l.description, source: l.source, examId: l.exam_id }} bigControls onDone={() => setPanel(null)} onCancel={() => setPanel(null)} />
                        )}
                      </PtFieldCard>
                    ) : (
                      <LineCard key={l.id} line={l} currency={ticket.currency} editable={editable} onEdit={() => setPanel({ kind: 'edit', line: l })} onRemove={() => setPanel({ kind: 'remove', line: l })} />
                    ),
                  )}
                </div>
              )}

              <PtFieldCard className="mt-3 flex items-center justify-between gap-3 px-4 py-4">
                <span className="text-[0.7rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal (before tax)</span>
                <span className="font-mono text-xl font-extrabold tabular-nums text-white" data-testid="ndt-field-ticket-subtotal">
                  {ndtSubtotalText(ticket.subtotal, ticket.line_count, ticket.currency)}
                </span>
              </PtFieldCard>

              {isDraft && !isOnline && (
                <div className="mt-3">
                  <OfflineNote what="change or sign this ticket" />
                </div>
              )}

              {editable && panel?.kind !== 'add' && (
                <button type="button" className={`${PT_FIELD_SECONDARY} mt-3`} onClick={() => setPanel({ kind: 'add' })} data-testid="ndt-field-add-line">
                  <Plus className="size-5" aria-hidden="true" />
                  Add from rate card
                </button>
              )}
              {editable && panel?.kind === 'add' && (
                <PtFieldCard className="mt-3 px-4 py-4">
                  <h2 className="mb-3 text-base font-bold text-white">Add from rate card</h2>
                  <NdtAddLineForm
                    ticketId={ticket.id}
                    customerId={ticket.customer_id}
                    currency={ticket.currency}
                    day={ticket.work_date ?? today}
                    canPrice={canPriceNdtTickets(user?.role)}
                    bigControls
                    submit={(args, cb) => addLine.mutate(args, cb)}
                    onDone={() => setPanel(null)}
                    onCancel={() => setPanel(null)}
                  />
                </PtFieldCard>
              )}

              {editable && (
                <button type="button" className={`${PT_FIELD_PRIMARY} mt-6`} onClick={() => setPanel({ kind: 'sign' })} data-testid="ndt-field-get-signature">
                  <PenLine className="size-5" aria-hidden="true" />
                  Get company man signature
                </button>
              )}
              {ticket.status === 'approved' && (
                <p className="mt-6 flex items-center gap-2 rounded-fi-control border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text">
                  <CheckCircle2 className="size-4 shrink-0" aria-hidden="true" />
                  Approved by the office.
                </p>
              )}
              <Link to={`/field/ndt/jobs/${ticket.job_id}`} className={`${PT_FIELD_SECONDARY} mt-3`}>
                Back to {ticket.job_number || 'the job'}
              </Link>

              {panel?.kind === 'sign' && <CompanyManSign ticket={ticket} onClose={() => setPanel(null)} />}
            </>
          )}
        </PtFieldQueryState>
      )}
    </PtFieldPage>
  );
}
